import React, { useState } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Loader2 } from "lucide-react";
import { JOIN_URL, LOGIN_URL } from "../lib/memberstack";

/**
 * Paywall — shown to guests once they've taken their three free pulls.
 *
 * If we can see an authenticated Memberstack session but it doesn't match a
 * qualifying plan (status === "unauthorised"), most of the time that's a
 * cross-subdomain cookie issue — the member just signed in on awake and the
 * loom hasn't picked up the session yet. We surface a "Refresh membership"
 * button so they don't have to reload the whole page.
 */
export default function Paywall({ accessStatus = "anonymous", onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const showRefresh = accessStatus !== "loading";

  async function handleRefresh() {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      // Tiny grace period so the spinner is visible even on fast networks
      setTimeout(() => setRefreshing(false), 450);
    }
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative pt-2 pb-2"
      data-testid="paywall-section"
      aria-labelledby="paywall-heading"
    >
      <div className="max-w-2xl mx-auto px-6">
        <div
          className="relative rounded-3xl border-2 shadow-[0_22px_60px_rgba(60,40,10,0.10)] overflow-hidden"
          style={{
            backgroundColor: "#fdfaf5",
            borderColor: "#3d2a14",
          }}
        >
          <div className="flex h-2.5" aria-hidden>
            <div className="flex-1" style={{ backgroundColor: "#fae2b5" }} />
            <div className="flex-1" style={{ backgroundColor: "#a7d5e5" }} />
            <div className="flex-1" style={{ backgroundColor: "#f6b1ae" }} />
            <div className="flex-1" style={{ backgroundColor: "#d0b6d1" }} />
          </div>

          <div className="px-7 md:px-10 py-9 md:py-11 text-center">
            <p
              className="text-xs uppercase tracking-[0.22em] text-weave-teal font-medium mb-4"
              data-testid="paywall-eyebrow"
            >
              The loom takes a breath
            </p>
            <h2
              id="paywall-heading"
              className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1a1a1a] leading-[1.1]"
              style={{ fontFamily: "Rubik, sans-serif" }}
              data-testid="paywall-headline"
            >
              You've pulled three threads.
            </h2>

            <p
              className="mt-5 text-base md:text-lg text-[#3a3a3a]/85 leading-relaxed max-w-xl mx-auto"
              data-testid="paywall-subtitle"
            >
              Want unlimited daily threads? Join The Weave's 1% Club — a small,
              brave room for people building something kinder.
            </p>

            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-lg mx-auto">
              <li
                className="flex items-start gap-2 px-3 py-2 rounded-xl border-2 text-sm"
                style={{ backgroundColor: "#fae2b5", borderColor: "#3d2a14" }}
              >
                <span
                  className="mt-1.5 block w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: "#b8862e" }}
                  aria-hidden
                />
                <span className="text-[#1a1a1a]/90 leading-snug">
                  Unlimited daily pulls
                </span>
              </li>
              <li
                className="flex items-start gap-2 px-3 py-2 rounded-xl border-2 text-sm"
                style={{ backgroundColor: "#a7d5e5", borderColor: "#3d2a14" }}
              >
                <span
                  className="mt-1.5 block w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: "#0f5b78" }}
                  aria-hidden
                />
                <span className="text-[#1a1a1a]/90 leading-snug">
                  The full 1% Club community
                </span>
              </li>
              <li
                className="flex items-start gap-2 px-3 py-2 rounded-xl border-2 text-sm"
                style={{ backgroundColor: "#f6b1ae", borderColor: "#3d2a14" }}
              >
                <span
                  className="mt-1.5 block w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: "#892620" }}
                  aria-hidden
                />
                <span className="text-[#1a1a1a]/90 leading-snug">
                  Capacity · connections · cash
                </span>
              </li>
            </ul>

            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={JOIN_URL}
                data-testid="paywall-join-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white shadow-[0_8px_22px_rgba(233,62,54,0.35)] border-2 transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{ backgroundColor: "#e93e36", borderColor: "#3d2a14" }}
              >
                Join the 1% Club
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
                  <path
                    d="M3 8h10M8 3l5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <a
                href={LOGIN_URL}
                data-testid="paywall-login-link"
                className="text-sm text-[#1a1a1a] weave-link"
              >
                Already a member? Log in
              </a>
            </div>

            {/* Subtle "I'm already signed in" recovery — re-checks the
                Memberstack session without a full page reload. Shows for
                every paywall view since the cookie may have arrived between
                the initial check and now (cross-subdomain timing). */}
            {showRefresh && (
              <div className="mt-6 flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  data-testid="paywall-refresh-button"
                  className="inline-flex items-center gap-2 text-xs text-[#3a3a3a]/65 hover:text-[#1a1a1a] underline underline-offset-4 transition-colors disabled:opacity-60"
                >
                  {refreshing ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Re-checking your 1% Club status…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3.5 h-3.5" />
                      Just signed in? Refresh my 1% Club status
                    </>
                  )}
                </button>
              </div>
            )}

            <p className="mt-6 text-xs text-[#3a3a3a]/60">
              The three you've pulled are still yours — pin the ones you want
              to keep below.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
